import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import Badge from "@/v2-backoffice-client/components/badge"
import LicenseStatusBadge from "@/v2-backoffice-client/license/LicenseStatusBadge"
import LicenseProductBadge from "@/v2-backoffice-client/license/product/LicenseProductBadge"
import { LICENSE_STATUS_COLUMN_SIZE } from "@/v2-backoffice-client/license/license.constants"
import LicenseAddOnGrantsRowToolbar from "@/v2-backoffice-client/license/addOns/catalog/view/LicenseAddOnGrantsRowToolbar"
import { useAddOnGrantsQuery } from "@/v2-backoffice-client/license/addOns/catalog/view/license.addOn.grants.query"
import { useLicenseStatusesQuery } from "@/v2-backoffice-client/license/license.statuses.query"
import type { AddOnGrantsQuery } from "@/v2-backoffice-client/license/addOns/catalog/view/__types__/license.addOn.grants.query"

export type LicenseAddOnGrantRow = AddOnGrantsQuery["getAddOnGrants"][number]

function createLicenseAddOnGrantColumns(input: {
  statusNamesByCode: Map<number, string>
}) {
  const { statusNamesByCode } = input

  return createColumns<LicenseAddOnGrantRow>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      CustomerName: {
        id: "CustomerName",
        label: "Customer",
        defaultSize: 200,
        // Push onto the sheet stack — add-on → customer and back.
        // App-created customers without a HubSpot id can't deep-link (the
        // customer panel is keyed by it).
        to: ({ row }) =>
          row.original.ExternalCustomerId
            ? `>>?v=customer:${row.original.ExternalCustomerId}`
            : undefined,
      },
      CarrierProductName: {
        id: "CarrierProductName",
        label: "With the license",
        description:
          "The customer's license this add-on goes with. Empty = the customer has no license it goes with yet, so the add-on gives them nothing until they do.",
        defaultSize: 180,
        enableGrouping: true,
        getValue: (row) => row.CarrierProductName ?? "",
        renderCell({ row }) {
          const value = row.original.CarrierProductName
          if (!value) {
            return (
              <Badge
                size="sm"
                variant="warning"
                tooltip="No license this add-on goes with. The add-on gives nothing until the customer has one."
              >
                No license yet
              </Badge>
            )
          }
          return (
            <LicenseProductBadge productName={value}>
              {value} license
            </LicenseProductBadge>
          )
        },
        exportValue({ row }) {
          return row.original.CarrierProductName ?? "No license yet"
        },
      },
      CarrierLicenseStatusCode: {
        id: "CarrierLicenseStatusCode",
        label: "License status",
        description:
          "The status of the license that carries the add-on. The add-on works while that license is live; an expired one means the add-on gives nothing right now.",
        defaultSize: LICENSE_STATUS_COLUMN_SIZE,
        enableGrouping: true,
        getValue: (row) =>
          row.CarrierLicenseStatusCode == null
            ? ""
            : (statusNamesByCode.get(row.CarrierLicenseStatusCode) ??
              String(row.CarrierLicenseStatusCode)),
        renderCell({ row }) {
          const grant = row.original
          if (grant.CarrierLicenseStatusCode == null) return null
          return (
            <LicenseStatusBadge
              license={{
                LicenseStatusCode: grant.CarrierLicenseStatusCode,
                TrialEndsAt: grant.CarrierTrialEndsAt,
                CancelledAt: grant.CarrierCancelledAt,
              }}
              statusName={statusNamesByCode.get(grant.CarrierLicenseStatusCode)}
            />
          )
        },
      },
      HasLiveCarrier: {
        id: "HasLiveCarrier",
        label: "Works now",
        description:
          "Yes = a live license carries the add-on and the customer can use what it unlocks. No = nothing until a license it goes with is live.",
        defaultSize: 90,
        enableGrouping: true,
        getValue: (row) => (row.HasLiveCarrier ? "Yes" : "No"),
        renderCell({ row }) {
          return row.original.HasLiveCarrier ? (
            <Badge size="sm" variant="positive">
              Yes
            </Badge>
          ) : (
            <Badge size="sm" variant="warning">
              No
            </Badge>
          )
        },
      },
      GrantedAt: {
        id: "GrantedAt",
        label: "Added",
        defaultSize: 90,
        renderCell({ row }) {
          const value = row.original.GrantedAt
          return value ? formatDateString(String(value)) : null
        },
      },
      GrantedByUserName: {
        id: "GrantedByUserName",
        label: "Added By",
        defaultSize: 140,
        renderCell({ row }) {
          const value = row.original.GrantedByUserName
          if (!value) {
            return <span className="text-muted-foreground">System</span>
          }
          return value
        },
        exportValue({ row }) {
          return row.original.GrantedByUserName ?? "System"
        },
      },
      CarrierLicenseId: {
        id: "CarrierLicenseId",
        label: "License Id",
        defaultSize: 70,
        defaultHidden: true,
      },
      CustomerId: {
        id: "CustomerId",
        label: "Customer Id",
        defaultSize: 70,
        defaultHidden: true,
      },
    },
  })
}

export type LicenseAddOnGrantsTableProps = Omit<
  TableProps<LicenseAddOnGrantRow>,
  "data" | "columns"
> & {
  addOnId: number
  addOnName: string
  /** Hooked by the sheet so the GrantCount badge refreshes with the list. */
  onRefresh?(): void
}

// The add-on panel's Customers tab: every customer who has this add-on,
// with the license that carries it for them and whether it works right now,
// who added it and when, with Remove on each row. Owns its list query — the
// rows load when the tab is first opened, not when the panel is.
function LicenseAddOnGrantsTable(props: LicenseAddOnGrantsTableProps) {
  const { className, addOnId, addOnName, onRefresh, ...tableProps } = props

  const { data, previousData, loading, error, refetch } = useAddOnGrantsQuery({
    variables: { addOnId },
  })
  // previousData keeps rows rendered through post-save evictions.
  const grants = (data ?? previousData)?.getAddOnGrants ?? []

  const { data: statusesData } = useLicenseStatusesQuery()
  const statusNamesByCode = useMemo(
    () =>
      new Map(
        (statusesData?.getLicenseStatuses ?? []).map((status) => [
          status.Code,
          status.Name,
        ]),
      ),
    [statusesData?.getLicenseStatuses],
  )

  const columns = useMemo(
    () => createLicenseAddOnGrantColumns({ statusNamesByCode }),
    [statusNamesByCode],
  )

  return (
    <Table
      className={cn("license-add-on-grants-table", className)}
      tableId="license-add-on-grants-table"
      data={grants}
      columns={columns}
      {...tableProps}
    >
      <Table.Toolbar>
        <div className="flex-1" />
        <Table.Search
          column="CustomerName"
          placeholder="Search by customer name..."
        />

        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu
          onRefresh={() => {
            void refetch()
            onRefresh?.()
          }}
          exportFileName={`add-on-${addOnId}-customers`}
        />
      </Table.Toolbar>

      <Table.FilterList />

      <Table.RowToolbar<LicenseAddOnGrantRow> column="CustomerName">
        {(grant) => (
          <LicenseAddOnGrantsRowToolbar
            addOnId={addOnId}
            addOnName={addOnName}
            grant={grant}
          />
        )}
      </Table.RowToolbar>

      <Table.Content isLoading={loading} error={error} />
    </Table>
  )
}

export default LicenseAddOnGrantsTable
